import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ClassManagement from './ClassManagement';
import InstructorManagement from './InstructorManagement';
import AdminSchedule from './AdminSchedule';
import UserBlockManagement from './UserBlockManagement';
import AttendanceManagement from './AttendanceManagement';
import CommunicationManagement from './CommunicationManagement';
import './Admin.css';

import { useAuth } from '../../context/AuthContext';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('classes');
  
  // Get the current user from AuthContext
  const { currentUser } = useAuth();
  
  // State for today's classes
  const [todaysClasses, setTodaysClasses] = useState([]);
  const [loadingTodaysClasses, setLoadingTodaysClasses] = useState(false);
  const [expandedClassId, setExpandedClassId] = useState(null);
  const [participants, setParticipants] = useState({});
  const [loadingParticipants, setLoadingParticipants] = useState({});
  
  // Define emails that can access the attendance tab
  const attendanceAccessEmails = ['szabo.roman2@yahoo.com', 'timikerekesova@gmail.com'];
  
  // Check if current user has access to attendance tab
  const hasAttendanceAccess = currentUser && attendanceAccessEmails.includes(currentUser.email);
  
  // Define emails that can access the communication tab
  const communicationAccessEmails = ['szabo.roman2@yahoo.com', 'timikerekesova@gmail.com', 'timea.friedrichova@gmail.com'];
  
  // Check if current user has access to communication tab
  const hasCommunicationAccess = currentUser && communicationAccessEmails.includes(currentUser.email);
  
  // Fetch today's classes
  const fetchTodaysClasses = async () => {
    if (!currentUser || !currentUser.is_admin) return;
    
    setLoadingTodaysClasses(true);
    try {
      const response = await axios.get('admin/my-todays-classes');
      if (response.data && response.data.success) {
        setTodaysClasses(response.data.classes || []);
      } else {
        // If response format is unexpected, set to empty array
        setTodaysClasses([]);
      }
    } catch (err) {
      console.error('Error fetching today\'s classes:', err);
      // On error, set to empty array to prevent crashes
      setTodaysClasses([]);
    } finally {
      setLoadingTodaysClasses(false);
    }
  };
  
  // Fetch participants for a class
  const fetchParticipants = async (classId) => {
    // If already loaded, don't fetch again
    if (participants[classId]) return;
    
    setLoadingParticipants(prev => ({ ...prev, [classId]: true }));
    try {
      const response = await axios.get(`admin/classes/${classId}/participants`);
      if (response.data && Array.isArray(response.data.participants)) {
        setParticipants(prev => ({ ...prev, [classId]: response.data.participants }));
      } else {
        setParticipants(prev => ({ ...prev, [classId]: [] }));
      }
    } catch (err) {
      console.error('Error fetching participants:', err);
      setParticipants(prev => ({ ...prev, [classId]: [] }));
    } finally {
      setLoadingParticipants(prev => ({ ...prev, [classId]: false }));
    }
  };
  
  // Toggle expansion of a class
  const toggleClassExpansion = async (classId) => {
    if (expandedClassId === classId) {
      // Collapse if already expanded
      setExpandedClassId(null);
    } else {
      // Expand and fetch participants if not already loaded
      setExpandedClassId(classId);
      await fetchParticipants(classId);
    }
  };
  
  // Fetch today's classes on mount and when user email changes
  useEffect(() => {
    if (currentUser && currentUser.is_admin) {
      fetchTodaysClasses();
    }
  }, [currentUser?.email]); // Only re-run if email changes (more stable than full object)
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    console.log('AdminDashboard mounted, activeTab:', activeTab);
    console.log('Current user email:', currentUser?.email);
  }, [activeTab, currentUser?.email]); // Use stable primitive values only

  return (
    <div className="admin-dashboard">
      <h1>{t('admin_dashboard')}</h1>
      
      {/* Today's Classes Panel */}
      {currentUser && currentUser.is_admin && (
        <div className="todays-classes-panel">
          <div className="todays-classes-header">
            <h2>{t('my_todays_classes')}</h2>
            <button 
              className="refresh-classes-btn"
              onClick={fetchTodaysClasses}
              disabled={loadingTodaysClasses}
              title={t('refresh')}
            >
              {loadingTodaysClasses ? '...' : '↻'}
            </button>
          </div>
          
          {loadingTodaysClasses ? (
            <div className="loading-message">{t('loading')}...</div>
          ) : todaysClasses.length > 0 ? (
            <div className="todays-classes-compact-list">
              {todaysClasses.filter(yogaClass => yogaClass && yogaClass.id).map((yogaClass) => {
                const isExpanded = expandedClassId === yogaClass.id;
                const classParticipants = participants[yogaClass.id] || [];
                const isLoadingParticipants = loadingParticipants[yogaClass.id];
                
                return (
                  <div key={yogaClass.id} className="class-bar-container">
                    <div 
                      className={`class-bar ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => toggleClassExpansion(yogaClass.id)}
                    >
                      <div className="class-bar-icon">
                        {isExpanded ? '▼' : '▶'}
                      </div>
                      <div className="class-bar-time">
                        {yogaClass.start_time || 'N/A'} - {yogaClass.end_time || 'N/A'}
                      </div>
                      <div className="class-bar-divider">│</div>
                      <div className="class-bar-name">
                        {yogaClass.title || yogaClass.name || 'Untitled'}
                      </div>
                      <div className="class-bar-divider">│</div>
                      <div className="class-bar-count">
                        {yogaClass.participant_count || 0}/{yogaClass.max_capacity || 0}
                      </div>
                    </div>
                    
                    <div className="class-bar-instructor">
                      {yogaClass.instructor_name || 'Unknown'}
                    </div>
                    
                    {isExpanded && (
                      <div className="class-bar-participants">
                        {isLoadingParticipants ? (
                          <div className="loading-inline">{t('loading_participants')}</div>
                        ) : classParticipants.length > 0 ? (
                          <div className="participants-inline-list">
                            {classParticipants.filter(p => p && p.user_id).map((participant, index) => (
                              <div key={participant.user_id} className="participant-inline-item">
                                <span className="participant-inline-number">{index + 1}.</span>
                                <span className="participant-inline-name">{participant.name}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="no-participants-inline">{t('no_participants_yet')}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-classes-today">
              <p>{t('no_classes_today')}</p>
            </div>
          )}
        </div>
      )}
      
      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'classes' ? 'active' : ''}`} 
          onClick={() => setActiveTab('classes')}
        >
          {t('class_schedule_management')}
        </button>
        <button 
          className={`admin-tab ${activeTab === 'instructors' ? 'active' : ''}`} 
          onClick={() => setActiveTab('instructors')}
        >
          {t('instructors_management')}
        </button>
        <button 
          className={`admin-tab ${activeTab === 'schedule' ? 'active' : ''}`} 
          onClick={() => setActiveTab('schedule')}
        >
          {t('admin_schedule')}
        </button>
        
        {/* Only show attendance tab to authorized users */}
        {hasAttendanceAccess && (
          <button 
            className={`admin-tab ${activeTab === 'attendance' ? 'active' : ''}`} 
            onClick={() => setActiveTab('attendance')}
          >
            {t('attendance_management')}
          </button>
        )}
        
        {/* Only show communication tab to authorized users */}
        {hasCommunicationAccess && (
          <button 
            className={`admin-tab ${activeTab === 'communication' ? 'active' : ''}`} 
            onClick={() => setActiveTab('communication')}
          >
            {t('communication_management') || 'Communication'}
          </button>
        )}
        
        <button 
          className={`admin-tab ${activeTab === 'blockuser' ? 'active' : ''}`} 
          onClick={() => setActiveTab('blockuser')}
        >
          {t('user_block_management')}
        </button>
        <Link to="/" className="back-to-site">{t('back_to_site')}</Link>
      </div>
      
      <div className="admin-content">
        {activeTab === 'classes' && <ClassManagement />}
        {activeTab === 'instructors' && <InstructorManagement />}
        {activeTab === 'schedule' && <AdminSchedule />}
        
        {/* Only render attendance component if user has access */}
        {activeTab === 'attendance' && hasAttendanceAccess && <AttendanceManagement />}
        
        {/* Only render communication component if user has access */}
        {activeTab === 'communication' && hasCommunicationAccess && <CommunicationManagement />}
        
        {activeTab === 'blockuser' && <UserBlockManagement />}
      </div>
    </div>
  );
};

export default AdminDashboard;
